import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { searchExercises } from '../../utils/exerciseSearch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PATTERN_COLORS = {
  squat: '#22c55e',
  hinge: '#f97316',
  horizontal_push: '#ef4444',
  vertical_push: '#f43f5e',
  horizontal_pull: '#3b82f6',
  vertical_pull: '#6366f1',
  isolation: '#a855f7',
  carry: '#14b8a6',
  rotation: '#eab308',
  anti_rotation: '#84cc16',
};

const CONFIDENCE_COLORS = {
  high: { bg: 'bg-emerald-900/40', border: 'border-emerald-500/50', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  medium: { bg: 'bg-amber-900/40', border: 'border-amber-500/50', text: 'text-amber-400', dot: 'bg-amber-400' },
  low: { bg: 'bg-red-900/40', border: 'border-red-500/50', text: 'text-red-400', dot: 'bg-red-400' },
};

const ClassificationReview = ({ language, t }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data from endpoints
  const [mpData, setMpData] = useState(null);
  const [familyData, setFamilyData] = useState(null);

  // UI state
  const [activeTab, setActiveTab] = useState('patterns'); // 'patterns' | 'families'
  const [confidenceFilter, setConfidenceFilter] = useState('all');
  const [viewFilter, setViewFilter] = useState('all'); // 'all' | 'conflicts' | 'unclassified'
  const [patternFilter, setPatternFilter] = useState('all');
  const [familyFilter, setFamilyFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Manual overrides (in memory only)
  const [overrides, setOverrides] = useState({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [mpRes, famRes] = await Promise.all([
        axios.get(`${API}/exercises/analysis/movement-pattern-preview`, { headers: getAuthHeaders() }),
        axios.get(`${API}/exercises/analysis/family-mapping-preview`, { headers: getAuthHeaders() }),
      ]);
      setMpData(mpRes.data);
      setFamilyData(famRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load classification data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOverride = (exerciseId, field, value) => {
    setOverrides(prev => ({
      ...prev,
      [exerciseId]: { ...(prev[exerciseId] || {}), [field]: value }
    }));
  };

  const clearOverride = (exerciseId, field) => {
    setOverrides(prev => {
      const updated = { ...prev };
      if (updated[exerciseId]) {
        delete updated[exerciseId][field];
        if (Object.keys(updated[exerciseId]).length === 0) delete updated[exerciseId];
      }
      return updated;
    });
  };

  const toggleGroup = (key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Filter exercises for patterns tab
  const getFilteredPatternExercises = () => {
    if (!mpData) return [];

    let exercises;
    if (viewFilter === 'conflicts') {
      exercises = mpData.ambiguous_exercises || [];
    } else if (viewFilter === 'unclassified') {
      exercises = mpData.unclassified_exercises || [];
    } else {
      exercises = [
        ...(mpData.classified_exercises || []),
        ...(mpData.unclassified_exercises || []),
      ];
    }

    if (confidenceFilter !== 'all') {
      exercises = exercises.filter(e => e.confidence === confidenceFilter);
    }
    if (patternFilter !== 'all') {
      exercises = exercises.filter(e => e.suggested_pattern === patternFilter);
    }
    if (searchTerm) {
      // Use shared search utility — classification preview objects use exercise_name, map to name for compatibility
      const mapped = exercises.map(e => ({ ...e, name: e.exercise_name }));
      const matched = searchExercises(searchTerm, mapped, language || 'en');
      const matchedIds = new Set(matched.map(e => e.exercise_id));
      exercises = exercises.filter(e => matchedIds.has(e.exercise_id));
    }
    return exercises;
  };

  // Group exercises by pattern
  const getGroupedByPattern = () => {
    const exercises = getFilteredPatternExercises();
    const groups = {};
    for (const ex of exercises) {
      const key = ex.suggested_pattern || 'unclassified';
      if (!groups[key]) groups[key] = [];
      groups[key].push(ex);
    }
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  };

  // Filter families
  const getFilteredFamilies = () => {
    if (!familyData) return [];

    if (viewFilter === 'conflicts') {
      return [{
        family: 'conflicts',
        description: 'Exercises matching multiple families',
        count: familyData.conflict_list?.length || 0,
        exercises: (familyData.conflict_list || []).map(c => c.exercise_name),
        _raw: familyData.conflict_list || [],
      }];
    }
    if (viewFilter === 'unclassified') {
      return [{
        family: 'unclassified',
        description: 'Exercises not matched to any family',
        count: familyData.unclassified_exercises?.length || 0,
        exercises: (familyData.unclassified_exercises || []).map(u => u.exercise_name),
        _raw: familyData.unclassified_exercises || [],
      }];
    }

    let families = familyData.families || [];
    if (familyFilter !== 'all') {
      families = families.filter(f => f.family === familyFilter);
    }
    return families.filter(f => f.count > 0);
  };

  const allPatterns = mpData ? Object.keys(mpData.by_pattern || {}) : [];
  const allFamilies = familyData ? (familyData.families || []).filter(f => f.count > 0).map(f => f.family) : [];
  const overrideCount = Object.keys(overrides).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="classification-loading">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-blue-200">Loading classification preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card p-6 text-center" data-testid="classification-error">
        <p className="text-red-400 mb-4">{error}</p>
        <button onClick={fetchData} className="btn-primary">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="classification-review">
      {/* Header stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Movement Patterns"
          value={`${mpData?.coverage?.coverage_percent || 0}%`}
          sub={`${mpData?.coverage?.classified || 0} / ${mpData?.total_exercises || 0}`}
          color="text-blue-400"
        />
        <StatCard
          label="High Confidence"
          value={mpData?.by_confidence?.high || 0}
          sub="name-rule matches"
          color="text-emerald-400"
        />
        <StatCard
          label="Family Coverage"
          value={`${familyData?.coverage?.coverage_percent || 0}%`}
          sub={`${familyData?.coverage?.classified || 0} / ${familyData?.total_exercises || 0}`}
          color="text-purple-400"
        />
        <StatCard
          label="Overrides"
          value={overrideCount}
          sub={overrideCount > 0 ? 'pending (not saved)' : 'none yet'}
          color={overrideCount > 0 ? 'text-amber-400' : 'text-gray-400'}
        />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-white/10 pb-2">
        <TabButton active={activeTab === 'patterns'} onClick={() => setActiveTab('patterns')}>
          Movement Patterns
        </TabButton>
        <TabButton active={activeTab === 'families'} onClick={() => setActiveTab('families')}>
          Exercise Families
        </TabButton>
        <div className="flex-1" />
        {overrideCount > 0 && (
          <span className="text-xs text-amber-400 bg-amber-900/30 px-2 py-1 rounded" data-testid="override-count">
            {overrideCount} override{overrideCount !== 1 ? 's' : ''} pending
          </span>
        )}
      </div>

      {/* Filters row */}
      <div className="glass-card p-3 flex flex-wrap gap-2 items-center">
        <input
          type="text"
          placeholder="Search exercises..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="input-field text-sm w-48 !mb-0"
          data-testid="classification-search"
        />

        <FilterSelect
          value={viewFilter}
          onChange={setViewFilter}
          testId="view-filter"
          options={[
            { value: 'all', label: 'All' },
            { value: 'conflicts', label: `Conflicts (${activeTab === 'patterns' ? mpData?.coverage?.ambiguous || 0 : familyData?.coverage?.conflicts || 0})` },
            { value: 'unclassified', label: `Unclassified (${activeTab === 'patterns' ? mpData?.coverage?.unclassified || 0 : familyData?.coverage?.unclassified || 0})` },
          ]}
        />

        <FilterSelect
          value={confidenceFilter}
          onChange={setConfidenceFilter}
          testId="confidence-filter"
          options={[
            { value: 'all', label: 'All Confidence' },
            { value: 'high', label: 'High' },
            { value: 'medium', label: 'Medium' },
            { value: 'low', label: 'Low' },
          ]}
        />

        {activeTab === 'patterns' && (
          <FilterSelect
            value={patternFilter}
            onChange={setPatternFilter}
            testId="pattern-filter"
            options={[
              { value: 'all', label: 'All Patterns' },
              ...allPatterns.map(p => ({ value: p, label: p.replace(/_/g, ' ') })),
            ]}
          />
        )}

        {activeTab === 'families' && viewFilter === 'all' && (
          <FilterSelect
            value={familyFilter}
            onChange={setFamilyFilter}
            testId="family-filter"
            options={[
              { value: 'all', label: 'All Families' },
              ...allFamilies.map(f => ({ value: f, label: f.replace(/_/g, ' ') })),
            ]}
          />
        )}
      </div>

      {/* Content */}
      {activeTab === 'patterns' ? (
        <PatternsView
          groups={getGroupedByPattern()}
          expandedGroups={expandedGroups}
          toggleGroup={toggleGroup}
          overrides={overrides}
          onOverride={handleOverride}
          onClearOverride={clearOverride}
          allPatterns={allPatterns}
        />
      ) : (
        <FamiliesView
          families={getFilteredFamilies()}
          expandedGroups={expandedGroups}
          toggleGroup={toggleGroup}
          overrides={overrides}
          onOverride={handleOverride}
          onClearOverride={clearOverride}
          allFamilies={allFamilies}
          viewFilter={viewFilter}
          searchTerm={searchTerm}
        />
      )}
    </div>
  );
};

/* ===== Sub-components ===== */

const StatCard = ({ label, value, sub, color }) => (
  <div className="glass-card p-3 text-center" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    <div className="text-xs text-gray-400 mt-0.5">{label}</div>
    <div className="text-xs text-gray-500">{sub}</div>
  </div>
);

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
      active ? 'text-white bg-white/10 border-b-2 border-blue-400' : 'text-gray-400 hover:text-gray-200'
    }`}
  >
    {children}
  </button>
);

const FilterSelect = ({ value, onChange, options, testId }) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value)}
    className="input-field text-sm py-1.5 w-auto min-w-[120px]"
    data-testid={testId}
  >
    {options.map(o => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

const PatternBadge = ({ pattern }) => {
  if (!pattern) return <span className="text-xs text-gray-500 italic">unclassified</span>;
  const color = PATTERN_COLORS[pattern] || '#6b7280';
  return (
    <span
      className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full"
      style={{ backgroundColor: `${color}20`, color, border: `1px solid ${color}40` }}
    >
      {pattern.replace(/_/g, ' ')}
    </span>
  );
};

const ConfidenceDot = ({ confidence }) => {
  const c = CONFIDENCE_COLORS[confidence] || CONFIDENCE_COLORS.low;
  return (
    <span className="inline-flex items-center gap-1 text-xs">
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      <span className={c.text}>{confidence}</span>
    </span>
  );
};

/* ===== Patterns View ===== */

const PatternsView = ({ groups, expandedGroups, toggleGroup, overrides, onOverride, onClearOverride, allPatterns }) => {
  if (groups.length === 0) {
    return <div className="text-center text-gray-400 py-10">No exercises match current filters.</div>;
  }

  return (
    <div className="space-y-2" data-testid="patterns-view">
      {groups.map(([pattern, exercises]) => {
        const isExpanded = expandedGroups.has(pattern);
        return (
          <div key={pattern} className="glass-card overflow-hidden">
            <button
              onClick={() => toggleGroup(pattern)}
              className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors text-left"
              data-testid={`pattern-group-${pattern}`}
            >
              <div className="flex items-center gap-3">
                <span className="text-gray-400 text-sm w-5">{isExpanded ? '▼' : '▶'}</span>
                <PatternBadge pattern={pattern === 'unclassified' ? null : pattern} />
                <span className="text-white font-medium">{exercises.length} exercises</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-400">
                <span>{exercises.filter(e => e.confidence === 'high').length} high</span>
                <span>{exercises.filter(e => e.confidence === 'medium').length} med</span>
                <span>{exercises.filter(e => e.conflict).length} conflicts</span>
              </div>
            </button>

            {isExpanded && (
              <div className="border-t border-white/5">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white/5 sticky top-0">
                      <tr>
                        <th className="text-left p-2 text-gray-400 font-medium">Exercise</th>
                        <th className="text-left p-2 text-gray-400 font-medium w-28">Category</th>
                        <th className="text-left p-2 text-gray-400 font-medium w-32">Pattern</th>
                        <th className="text-left p-2 text-gray-400 font-medium w-20">Conf.</th>
                        <th className="text-left p-2 text-gray-400 font-medium w-28">Source</th>
                        <th className="text-left p-2 text-gray-400 font-medium w-40">Override</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exercises.map(ex => {
                        const hasOverride = overrides[ex.exercise_id]?.movement_pattern;
                        const isConflict = ex.conflict;
                        return (
                          <tr
                            key={ex.exercise_id}
                            className={`border-t border-white/5 hover:bg-white/5 ${isConflict ? 'bg-amber-900/10' : ''}`}
                            data-testid={`pattern-row-${ex.exercise_id}`}
                          >
                            <td className="p-2">
                              <span className="text-white">{ex.exercise_name}</span>
                              {isConflict && (
                                <span className="ml-2 text-xs text-amber-400" title={`Conflicts: ${ex.conflict_patterns?.join(', ')}`}>
                                  conflict
                                </span>
                              )}
                            </td>
                            <td className="p-2 text-gray-400 text-xs">{ex.category}</td>
                            <td className="p-2">
                              {hasOverride ? (
                                <div className="flex items-center gap-1">
                                  <PatternBadge pattern={hasOverride} />
                                  <span className="text-amber-400 text-xs">(override)</span>
                                </div>
                              ) : (
                                <PatternBadge pattern={ex.suggested_pattern} />
                              )}
                            </td>
                            <td className="p-2"><ConfidenceDot confidence={ex.confidence} /></td>
                            <td className="p-2 text-xs text-gray-500">{ex.rule_source?.split(':')[0]}</td>
                            <td className="p-2">
                              <div className="flex items-center gap-1">
                                <select
                                  value={hasOverride || ''}
                                  onChange={e => {
                                    if (e.target.value) {
                                      onOverride(ex.exercise_id, 'movement_pattern', e.target.value);
                                    } else {
                                      onClearOverride(ex.exercise_id, 'movement_pattern');
                                    }
                                  }}
                                  className="bg-transparent border border-white/10 rounded text-xs text-gray-300 py-0.5 px-1"
                                  data-testid={`override-select-${ex.exercise_id}`}
                                >
                                  <option value="">—</option>
                                  {allPatterns.map(p => (
                                    <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
                                  ))}
                                </select>
                                {hasOverride && (
                                  <button
                                    onClick={() => onClearOverride(ex.exercise_id, 'movement_pattern')}
                                    className="text-red-400 hover:text-red-300 text-xs"
                                    title="Clear override"
                                  >
                                    x
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ===== Families View ===== */

const FamiliesView = ({ families, expandedGroups, toggleGroup, overrides, onOverride, onClearOverride, allFamilies, viewFilter, searchTerm }) => {
  if (families.length === 0) {
    return <div className="text-center text-gray-400 py-10">No families match current filters.</div>;
  }

  return (
    <div className="space-y-2" data-testid="families-view">
      {families.map(fam => {
        const key = `fam-${fam.family}`;
        const isExpanded = expandedGroups.has(key);
        const isSpecial = fam.family === 'conflicts' || fam.family === 'unclassified';
        const cb = fam.confidence_breakdown;

        let displayExercises = fam.exercises || [];
        if (searchTerm) {
          const mapped = displayExercises.map(name => ({
            name: typeof name === 'string' ? name : name?.exercise_name || '',
            _original: name,
          }));
          const matched = searchExercises(searchTerm, mapped, 'en');
          displayExercises = matched.map(m => m._original);
        }

        return (
          <div key={fam.family} className="glass-card overflow-hidden">
            <button
              onClick={() => toggleGroup(key)}
              className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors text-left"
              data-testid={`family-group-${fam.family}`}
            >
              <div className="flex items-center gap-3">
                <span className="text-gray-400 text-sm w-5">{isExpanded ? '▼' : '▶'}</span>
                <span className="text-white font-medium">{fam.family.replace(/_/g, ' ')}</span>
                <span className="text-gray-400 text-sm">({fam.count})</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-400">
                {!isSpecial && cb && (
                  <>
                    <span className="text-emerald-400">{cb.high} H</span>
                    <span className="text-amber-400">{cb.medium} M</span>
                    <span className="text-red-400">{cb.low} L</span>
                  </>
                )}
                <span className="text-gray-500 italic max-w-[200px] truncate">{fam.description}</span>
              </div>
            </button>

            {isExpanded && (
              <div className="border-t border-white/5 max-h-80 overflow-y-auto">
                {isSpecial && viewFilter === 'conflicts' ? (
                  <table className="w-full text-sm">
                    <thead className="bg-white/5 sticky top-0">
                      <tr>
                        <th className="text-left p-2 text-gray-400 font-medium">Exercise</th>
                        <th className="text-left p-2 text-gray-400 font-medium w-40">Matched Families</th>
                        <th className="text-left p-2 text-gray-400 font-medium w-28">Resolved To</th>
                        <th className="text-left p-2 text-gray-400 font-medium w-40">Override</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(fam._raw || []).map(c => {
                        const hasOverride = overrides[c.exercise_id]?.exercise_family;
                        return (
                          <tr key={c.exercise_id} className="border-t border-white/5 hover:bg-white/5">
                            <td className="p-2 text-white">{c.exercise_name}</td>
                            <td className="p-2 text-xs text-gray-400">{c.matched_families?.join(', ')}</td>
                            <td className="p-2 text-xs"><PatternBadge pattern={c.resolved_to} /></td>
                            <td className="p-2">
                              <select
                                value={hasOverride || ''}
                                onChange={e => {
                                  if (e.target.value) onOverride(c.exercise_id, 'exercise_family', e.target.value);
                                  else onClearOverride(c.exercise_id, 'exercise_family');
                                }}
                                className="bg-transparent border border-white/10 rounded text-xs text-gray-300 py-0.5 px-1"
                              >
                                <option value="">—</option>
                                {allFamilies.map(f => (
                                  <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : isSpecial && viewFilter === 'unclassified' ? (
                  <table className="w-full text-sm">
                    <thead className="bg-white/5 sticky top-0">
                      <tr>
                        <th className="text-left p-2 text-gray-400 font-medium">Exercise</th>
                        <th className="text-left p-2 text-gray-400 font-medium w-24">Category</th>
                        <th className="text-left p-2 text-gray-400 font-medium w-28">Pattern</th>
                        <th className="text-left p-2 text-gray-400 font-medium w-40">Muscles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(fam._raw || []).filter(u => {
                        if (!searchTerm) return true;
                        const mapped = [{ name: u.exercise_name || '', ...u }];
                        return searchExercises(searchTerm, mapped, 'en').length > 0;
                      }).map(u => (
                        <tr key={u.exercise_id} className="border-t border-white/5 hover:bg-white/5">
                          <td className="p-2 text-white">{u.exercise_name}</td>
                          <td className="p-2 text-xs text-gray-400">{u.category}</td>
                          <td className="p-2"><PatternBadge pattern={u.movement_pattern} /></td>
                          <td className="p-2 text-xs text-gray-500">{(u.primary_muscle_groups || []).join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-3 flex flex-wrap gap-1.5">
                    {displayExercises.map((name, i) => (
                      <span key={i} className="text-xs bg-white/5 text-gray-300 px-2 py-1 rounded">
                        {typeof name === 'string' ? name : name?.exercise_name || 'Unknown'}
                      </span>
                    ))}
                    {displayExercises.length === 0 && (
                      <span className="text-gray-500 text-xs italic">No matches</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ClassificationReview;
